import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, User, Crown, Users, Mail, Power, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import api from '../../services/api';
import { getClientPermissions } from '../../lib/clientPermissions';

const ROLE_META = {
  owner: { label: 'Owner', icon: Crown, badge: 'bg-amber-100 text-amber-800 border-amber-200', tint: 'bg-amber-100',
    desc: 'Full access — manage the whole team (including owners), place orders, pay & view invoices, and edit account settings.' },
  manager: { label: 'Manager', icon: Users, badge: 'bg-blue-100 text-blue-800 border-blue-200', tint: 'bg-blue-100',
    desc: 'Manage team members & other managers, place orders, pay & view invoices, edit profile. Cannot manage owners.' },
  member: { label: 'Member', icon: User, badge: 'bg-gray-100 text-gray-800 border-gray-200', tint: 'bg-gray-100',
    desc: 'A regular team user — can place orders and view invoices & reports. Cannot manage the team.' },
};

const STATUS_BADGE = {
  invited: 'bg-amber-100 text-amber-700',
  active: 'bg-green-100 text-green-700',
  disabled: 'bg-gray-200 text-gray-600',
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const sec = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
};

const RoleIcon = ({ role, className }) => {
  const Icon = (ROLE_META[role] || ROLE_META.member).icon;
  return <Icon className={className} />;
};

const Team = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [addData, setAddData] = useState({ email: '', name: '', account_role: 'member' });
  const [inviteLink, setInviteLink] = useState(null);

  const perms = getClientPermissions();
  const canManage = perms.can_manage_users;
  const isOwner = perms.isOwner;
  const assignableRoles = isOwner ? ['owner', 'manager', 'member'] : ['manager', 'member'];

  const fetchTeam = useCallback(async () => {
    try {
      const response = await api.get('/client-portal/team/users');
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const handleAddUser = async () => {
    if (!addData.email || !addData.name) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/client-portal/team/users', addData);
      setShowAddDialog(false);
      setAddData({ email: '', name: '', account_role: 'member' });
      fetchTeam();
      if (res.data?.email_sent === false && res.data?.invite_link) {
        setInviteLink(res.data.invite_link);
        toast.warning(res.data.message || 'User added, but the invite email could not be sent.');
      } else {
        toast.success(res.data?.message || `Invitation sent to ${addData.email}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateRole = async () => {
    setSubmitting(true);
    try {
      await api.put(`/client-portal/team/users/${selectedMember.id}`, {
        account_role: selectedMember.account_role,
      });
      toast.success('Role updated');
      setShowEditDialog(false);
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (member) => {
    const next = member.status === 'disabled' ? 'active' : 'disabled';
    try {
      await api.post(`/client-portal/team/users/${member.id}/status`, { status: next });
      toast.success(`User ${next === 'active' ? 'enabled' : 'disabled'}`);
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleResend = async (member) => {
    try {
      const res = await api.post(`/client-portal/team/users/${member.id}/resend-invite`);
      if (res.data?.email_sent === false && res.data?.invite_link) {
        setInviteLink(res.data.invite_link);
        toast.warning(res.data.message || 'Could not send the email. Share the link instead.');
      } else {
        toast.success(res.data?.message || `Invitation re-sent to ${member.email}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const handleRemove = async () => {
    setSubmitting(true);
    try {
      await api.delete(`/client-portal/team/users/${selectedMember.id}`);
      toast.success('User removed');
      setShowDeleteDialog(false);
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    } finally {
      setSubmitting(false);
    }
  };

  // A manager cannot act on owners; owners can act on anyone.
  const canActOn = (m) => canManage && (isOwner || m.account_role !== 'owner');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-team-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
          <p className="text-gray-500">Add and manage the logins for your account</p>
        </div>
        {canManage && (
          <Button onClick={() => setShowAddDialog(true)} className="bg-amber-500 hover:bg-amber-600" data-testid="invite-member-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        )}
      </div>

      {/* Role Legend */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-gray-500">Role Capabilities</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {['owner', 'manager', 'member'].map((r) => {
              const m = ROLE_META[r];
              return (
                <div key={r} className={`flex items-start gap-3 p-3 ${m.tint} rounded-lg`}>
                  <RoleIcon role={r} className="h-5 w-5 mt-0.5 text-gray-700" />
                  <div>
                    <div className="font-medium text-gray-800">{m.label}</div>
                    <div className="text-sm text-gray-600">{m.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Members */}
      <Card>
        <CardHeader>
          <CardTitle>Account Logins ({members.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {members.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-4 hover:bg-gray-50" data-testid={`team-row-${member.id}`}>
                <div className="flex items-center gap-4">
                  <div className={`h-12 w-12 rounded-full flex items-center justify-center ${(ROLE_META[member.account_role] || ROLE_META.member).tint}`}>
                    <RoleIcon role={member.account_role} className="h-5 w-5 text-gray-700" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{member.name}</span>
                    </div>
                    <div className="text-sm text-gray-500">{member.email}</div>
                    {member.status === 'invited' && (
                      <div className="text-xs text-amber-600 mt-0.5 flex items-center gap-1" data-testid={`team-invite-sent-${member.id}`}>
                        <Mail className="h-3 w-3" />
                        Invite sent {timeAgo(member.invite_sent_at) || 'recently'}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${STATUS_BADGE[member.status] || STATUS_BADGE.active} text-xs`} data-testid={`team-status-${member.id}`}>
                    {member.status === 'invited' && <Mail className="h-3 w-3 mr-1" />}
                    <span className="capitalize">{member.status === 'invited' ? 'Invitation pending' : member.status}</span>
                  </Badge>
                  <Badge className={`${(ROLE_META[member.account_role] || ROLE_META.member).badge} flex items-center gap-1`}>
                    <RoleIcon role={member.account_role} className="h-3.5 w-3.5" />
                    <span>{(ROLE_META[member.account_role] || ROLE_META.member).label}</span>
                  </Badge>
                  {canActOn(member) && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" title="Edit role"
                        data-testid={`team-edit-${member.id}`}
                        onClick={() => { setSelectedMember(member); setShowEditDialog(true); }}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      {member.status === 'invited' && (
                        <Button variant="outline" size="sm" title="Resend invitation email"
                          className="h-8 border-amber-300 text-amber-700 hover:bg-amber-50"
                          data-testid={`team-resend-${member.id}`}
                          onClick={() => handleResend(member)}>
                          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Resend
                        </Button>
                      )}
                      <Button variant="ghost" size="sm"
                        title={member.status === 'disabled' ? 'Enable' : 'Disable'}
                        className={member.status === 'disabled' ? 'text-green-600' : 'text-gray-500'}
                        data-testid={`team-toggle-${member.id}`}
                        onClick={() => handleToggleStatus(member)}>
                        <Power className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600"
                        title="Remove"
                        data-testid={`team-remove-${member.id}`}
                        onClick={() => { setSelectedMember(member); setShowDeleteDialog(true); }}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {members.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No logins yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
            <DialogDescription>Invite a new login to your wholesale account. They'll get an email to set their password.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email Address *</Label>
              <Input type="email" value={addData.email} data-testid="add-user-email"
                onChange={(e) => setAddData({ ...addData, email: e.target.value })}
                placeholder="colleague@company.com" />
            </div>
            <div>
              <Label>Full Name *</Label>
              <Input value={addData.name} data-testid="add-user-name"
                onChange={(e) => setAddData({ ...addData, name: e.target.value })}
                placeholder="Jane Smith" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={addData.account_role} onValueChange={(v) => setAddData({ ...addData, account_role: v })}>
                <SelectTrigger data-testid="add-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {assignableRoles.map((r) => (
                    <SelectItem key={r} value={r} data-testid={`add-user-role-${r}`}>
                      <div className="flex items-center gap-2">
                        <RoleIcon role={r} className="h-4 w-4 text-gray-600" />
                        <span>{ROLE_META[r].label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">{ROLE_META[addData.account_role].desc}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddUser} disabled={submitting} className="bg-amber-500 hover:bg-amber-600" data-testid="add-user-submit">
              <Mail className="h-4 w-4 mr-2" />
              {submitting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Role</DialogTitle>
            <DialogDescription>Set the role for {selectedMember?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Role</Label>
              <Select value={selectedMember?.account_role}
                onValueChange={(v) => setSelectedMember({ ...selectedMember, account_role: v })}>
                <SelectTrigger data-testid="edit-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {assignableRoles.map((r) => (
                    <SelectItem key={r} value={r} data-testid={`edit-role-${r}`}>{ROLE_META[r].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">{ROLE_META[selectedMember?.account_role]?.desc}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateRole} disabled={submitting} className="bg-amber-500 hover:bg-amber-600" data-testid="save-team-member-btn">
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove User</DialogTitle>
            <DialogDescription>
              Remove {selectedMember?.name} from your account? Their login will be deleted and they'll lose all access.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button onClick={handleRemove} disabled={submitting} className="bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-remove-btn">
              {submitting ? 'Removing...' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Fallback invite-link dialog (shown when the email could not be delivered) */}
      <Dialog open={!!inviteLink} onOpenChange={(o) => { if (!o) setInviteLink(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Share this setup link</DialogTitle>
            <DialogDescription>
              We couldn't deliver the invitation email right now. Copy this link and send it to the user — it lets them set their password (valid for 72 hours).
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input readOnly value={inviteLink || ''} data-testid="invite-link-input" onFocus={(e) => e.target.select()} />
            <Button
              onClick={() => { navigator.clipboard?.writeText(inviteLink || ''); toast.success('Link copied'); }}
              className="bg-amber-500 hover:bg-amber-600 shrink-0"
              data-testid="copy-invite-link-btn"
            >
              Copy
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteLink(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Team;
